import { useLocation, Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentToken, selectCurrentUser } from '../authSlice';
import { UserRole } from '../types';
import { User } from '../../users/types';

interface RequireAuthProps {
  allowedRoles?: UserRole[]; // Usar el tipo UserRole definido en auth/types.ts
  requiredBots?: number[];
}

// Función auxiliar para verificar permisos de bot
const hasBotPermission = (user: User | null, botNumber: number): boolean => {
  if (!user) return false;
  const key = `bot${botNumber}_enabled` as keyof User;
  return Boolean(user[key]);
};

export const RequireAuth = ({ allowedRoles, requiredBots }: RequireAuthProps) => {
  const token = useSelector(selectCurrentToken);
  const user = useSelector(selectCurrentUser);
  const location = useLocation();

  if (!token || !user) {
    // Usuario no autenticado, redirigir a login
    // Guardamos la ubicación a la que intentaba ir para redirigir de vuelta después del login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role as UserRole)) {
    // Usuario autenticado pero no tiene el rol requerido
    // Redirigir a una página de "No autorizado" o simplemente al dashboard
    // Aquí redirigimos al dashboard como ejemplo simple
    // Podrías crear una página específica /unauthorized
    return <Navigate to="/" replace />; // O a /unauthorized
  }

  // Verificar permisos de bots si se especifican
  if (requiredBots && requiredBots.length > 0) {
    // Superadmin siempre tiene acceso
    if (user.role !== 'superadmin') {
      // Verificar que tenga al menos un permiso de bot requerido
      const hasAnyBotPermission = requiredBots.some(botNumber => 
        hasBotPermission(user, botNumber)
      );

      if (!hasAnyBotPermission) {
        // Redirigir al perfil si no tiene los permisos necesarios
        return <Navigate to="/perfil" replace />;
      }
    }
  }

  // Usuario autenticado y (si se especificó) tiene el rol permitido
  return <Outlet />; // Renderiza el componente hijo (la página protegida)
}; 